import React, { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX } from "lucide-react";

/**
 * Sitewide ambient audio player.
 *
 * Loops `/media/ambient.mp3` (extracted from the hero devotional film) across
 * every route. Autoplay policy compliant — the audio element mounts muted, and
 * only unmutes after the user clicks the floating toggle. State is persisted
 * in localStorage so the choice survives navigation and reloads within a
 * session.
 *
 * The hero video keeps playing muted (it has no per-band sound toggle now)
 * because the ambient track carries the same music site-wide and we do not
 * want the two overlapping.
 */
const KEY = "sm.ambient.on";

export function AmbientAudio() {
  const audioRef = useRef(null);
  const [on, setOn] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(KEY) === "1";
  });
  const [available, setAvailable] = useState(true);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    el.volume = 0.35;
    if (on) {
      el.muted = false;
      el.play().catch(() => {
        // Autoplay blocked — reset the toggle to off until the user tries again.
        setOn(false);
        window.localStorage.setItem(KEY, "0");
      });
    } else {
      el.muted = true;
      el.pause();
    }
  }, [on]);

  const toggle = () => {
    const next = !on;
    setOn(next);
    window.localStorage.setItem(KEY, next ? "1" : "0");
  };

  if (!available) return null;

  return (
    <>
      <audio
        ref={audioRef}
        src="/media/ambient.mp3"
        loop
        preload="auto"
        aria-hidden="true"
        onError={() => setAvailable(false)}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={on ? "Mute the ambient music" : "Play the ambient music"}
        aria-pressed={on}
        data-testid="ambient-audio-toggle"
        className="ambient-toggle"
        title={on ? "Ambient music is on — click to mute" : "Play devotional flute across the site"}
      >
        {on ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
        <span className="ambient-toggle__label">{on ? "Sound on" : "Sound off"}</span>
      </button>
    </>
  );
}
